import { inject, Service } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '@authentication';
import { EventBusService } from '@shared';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { SseService } from '../../core/sse';
import { BaseService } from '../../services/base.service';
import { CLOCK_EVENTS } from './app-clock-events.model';
import { ClockMessage, parseClockTime } from './app-clock.model';

@Service()
export class AppClockService extends BaseService {
	private readonly sseService = inject(SseService);
	private readonly authService = inject(AuthService);
	private readonly eventBus = inject(EventBusService);

	/** Resolves once, as soon as authentication finishes loading. */
	private readonly authReady$ = toObservable(this.authService.waitingForLogin).pipe(
		filter(waitingForLogin => !waitingForLogin),
		take(1)
	);

	/**
	 * Opens (or reuses) the `clock` SSE topic and returns the current server time.
	 *
	 * Waits for authentication to finish loading first: this connects as soon as
	 * `HeaderComponent` mounts (before any route/`AuthGuard` has resolved), so opening
	 * it immediately would send the request with no token yet and get a 401.
	 *
	 * Each time also goes onto the event bus (`clock.time`), so consumers that
	 * only need the current time — `MarketSessionService`, deciding whether the
	 * market is trading — can read it without opening a connection of their own
	 * or pulling the SSE and auth stack into their own dependencies.
	 */
	connect(): Observable<Date> {
		const url = new URL('api/v1/subscribe', this.sseApiPath);
		url.searchParams.append('topics', 'clock');

		return this.authReady$.pipe(
			switchMap(() => this.sseService.connect<ClockMessage>(url.toString())),
			// The first event on any SSE connection is a handshake ack (e.g. {"type":"ack","data":{"succeeded":true}}),
			// not a clock payload — skip it so it's never parsed as one.
			filter(message => message.type !== 'ack'),
			map(message => (typeof message.data?.time === 'string' ? parseClockTime(message.data.time) : null)),
			// A message whose `time` is missing/non-string, or that doesn't parse to a valid date,
			// is skipped rather than propagated — one malformed payload must not break the stream.
			filter((time): time is Date => time !== null && !Number.isNaN(time.getTime())),
			tap(time => this.eventBus.dispatch<number>(CLOCK_EVENTS.TIME, time.getTime()))
		);
	}
}
